import type { BuildPhase } from "@hatch/shared";

/**
 * Phase copy. Plain language, present tense, honest about what is happening —
 * no padding to make a short phase feel substantial.
 */
export const PHASE_LABELS: Record<BuildPhase, string> = {
  queued: "Waiting",
  scaffolding: "Setting up",
  backend: "Backend",
  frontend: "Frontend",
  deploying: "Deploying",
  done: "Done",
};

/** Phase labels are also step labels when a phase boundary appears inline. */
export function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase as BuildPhase] ?? phase;
}

export function describeElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
}

/** Clock time for a transcript row, e.g. "14:07". */
export function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * Time since the build began, e.g. "t+2m 4s".
 *
 * The other stamp a feed can use. Wall-clock says when something happened;
 * this says how far into the build it happened, which is the more useful of the
 * two when the question is "how long did that take" rather than "what time is
 * it".
 *
 * Clamped at zero. `createdAt` arrives from the snapshot fetch and `at` from
 * the stream — both server-assigned, but by different paths — and a negative
 * stamp would turn an ordering hiccup into a visible nonsense reading.
 */
export function relativeStamp(at: number, createdAt: number): string {
  return `t+${describeElapsed(Math.max(0, at - createdAt))}`;
}
